import SunAccent from "@/components/feature/SunAccent";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { useTripPlannerData } from "@/hooks/usePublicGuide";
import type { TripPoolItem } from "@/mocks/tripPlanner";
import { useTripPlan } from "@/hooks/useTripPlan";
import { buildItineraryText, formatRupees, summarizeDay, summarizeItinerary } from "@/pages/plan-trip/planUtils";

const MAX_DAYS = 4;

const typeStyles: Record<string, string> = {
  experience: "bg-primary-100 text-primary-700",
  farm: "bg-secondary-100 text-secondary-700",
  culture: "bg-accent-100 text-accent-700",
};

const typeLabels: Record<string, string> = {
  experience: "Experience",
  farm: "Farm",
  culture: "Konaseema",
};

export default function TripBuilder({ onBuildItinerary }: { onBuildItinerary?: () => void }) {
  const { pool, filters } = useTripPlannerData();
  const [filter, setFilter] = useState("all");
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const {
    dayCount,
    activeDay,
    plan,
    setDayCount,
    setActiveDay,
    toggleItem,
    removeItem,
    clearPlan,
  } = useTripPlan();

  const itemById = useMemo(() => {
    const map: Record<string, TripPoolItem> = {};
    pool.forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [pool]);

  const dayOf = (id: string) => {
    for (let d = 0; d < dayCount; d += 1) {
      if ((plan[d] || []).includes(id)) return d;
    }
    return -1;
  };

  const handleToggle = (id: string) => toggleItem(id, activeDay);
  const handleRemove = (day: number, id: string) => removeItem(day, id);
  const handleSetDays = (count: number) => setDayCount(count);
  const handleClear = () => clearPlan();

  const totalStops = Object.values(plan).reduce((sum, list) => sum + list.length, 0);

  const summary = useMemo(() => summarizeItinerary(plan, dayCount), [plan, dayCount]);

  const filteredPool =
    filter === "all" ? pool : pool.filter((item) => item.type === filter);

  const shareText = useMemo(() => buildItineraryText(plan, dayCount), [plan, dayCount]);
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("My Rythugaru Konaseema itinerary")}&body=${encodeURIComponent(
    shareText,
  )}`;

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section id="trip-builder" className="sun-glaze bg-secondary-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-12">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              Build Your Itinerary
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
            Mix, Match & Map Out Your Days
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Choose how many days you have, tap the experiences and farms you'd love, and drop each
            one into a day. Your plan updates live on the right.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-start">
          <Reveal className="lg:col-span-7">
            <div className="bg-background-50 rounded-lg border border-secondary-200 p-6 md:p-7">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                {filters.map((option) => {
                  const isActive = option.id === filter;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setFilter(option.id)}
                      className={`text-sm font-medium px-4 py-2 rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                        isActive
                          ? "bg-primary-500 border-primary-500 text-background-50"
                          : "bg-background-100 border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[34rem] overflow-y-auto pr-1">
                {filteredPool.map((item) => {
                  const day = dayOf(item.id);
                  const inPlan = day >= 0;
                  return (
                    <div
                      key={item.id}
                      className={`rounded-md border transition-colors ${
                        inPlan
                          ? "border-accent-400 bg-accent-50"
                          : "border-background-200 bg-background-100 hover:border-accent-300"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => handleToggle(item.id)}
                        className="w-full text-left p-4 cursor-pointer"
                      >
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={`w-10 h-10 flex items-center justify-center rounded-full shrink-0 ${
                            typeStyles[item.type]
                          }`}
                        >
                          <i className={`${item.icon} text-xl`}></i>
                        </span>
                        <span
                          className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 ${
                            inPlan
                              ? "bg-accent-500 text-background-50"
                              : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          <i className={`${inPlan ? "ri-check-line" : "ri-add-line"} text-base`}></i>
                        </span>
                      </div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mt-3">
                        {typeLabels[item.type]}
                      </p>
                      <h3 className="font-heading text-base text-foreground-950 font-semibold leading-snug mt-1">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed mt-1.5">
                        {item.meta}
                      </p>
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground-700 mt-3">
                        <i className="ri-time-line text-accent-600"></i>
                        {item.duration}
                        {inPlan && (
                          <span className="ml-auto text-accent-700">Day {day + 1}</span>
                        )}
                      </p>
                      </button>
                      {item.type === "experience" && (
                        <div className="px-4 pb-4">
                          <Link
                            to={`${item.href}#booking-calendar`}
                            className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-xs font-semibold px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-calendar-check-line text-base"></i>
                            Reserve Your Date
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={120}>
            <div className="lg:sticky lg:top-24 bg-primary-900 rounded-lg p-6 md:p-7">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-300">
                    Your Itinerary
                  </span>
                  <h3 className="font-heading text-2xl text-background-50 font-semibold mt-1.5">
                    {totalStops === 0 ? "Still empty" : `${totalStops} stops planned`}
                  </h3>
                </div>
                {totalStops > 0 && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-background-200 hover:text-background-50 border border-primary-700 hover:border-primary-600 px-3 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                    Clear
                  </button>
                )}
              </div>

              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-background-300 mb-3">
                  Number of days
                </p>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: MAX_DAYS }, (_, i) => i + 1).map((count) => {
                    const isActive = count === dayCount;
                    return (
                      <button
                        key={count}
                        type="button"
                        onClick={() => handleSetDays(count)}
                        className={`text-sm font-semibold px-4 py-2 rounded-md border transition-colors whitespace-nowrap cursor-pointer ${
                          isActive
                            ? "bg-accent-500 border-accent-500 text-background-50"
                            : "bg-primary-800 border-primary-700 text-background-100 hover:border-accent-500"
                        }`}
                      >
                        {count} {count === 1 ? "day" : "days"}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-5">
                {Array.from({ length: dayCount }, (_, d) => d).map((d) => {
                  const isActive = d === activeDay;
                  const count = (plan[d] || []).length;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setActiveDay(d)}
                      className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md border transition-colors whitespace-nowrap cursor-pointer ${
                        isActive
                          ? "bg-background-50 border-background-50 text-primary-900"
                          : "bg-primary-800 border-primary-700 text-background-100 hover:border-accent-500"
                      }`}
                    >
                      Day {d + 1}
                      <span
                        className={`text-[11px] font-bold w-5 h-5 flex items-center justify-center rounded-full ${
                          isActive ? "bg-primary-500 text-background-50" : "bg-primary-700 text-background-100"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-background-300 mt-3">
                Adding items to <span className="font-semibold text-accent-300">Day {activeDay + 1}</span>
              </p>

              <div className="mt-5 space-y-4 max-h-[22rem] overflow-y-auto pr-1">
                {(plan[activeDay] || []).length === 0 ? (
                  <div className="border border-dashed border-primary-700 rounded-md px-5 py-8 text-center">
                    <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-800 text-accent-300">
                      <i className="ri-add-circle-line text-2xl"></i>
                    </span>
                    <p className="text-sm text-background-200 leading-relaxed mt-4">
                      Nothing here yet. Tap experiences on the left to add them to Day {activeDay + 1}.
                    </p>
                    {totalStops === 0 && onBuildItinerary && (
                      <>
                        <p className="text-xs text-background-300 leading-relaxed mt-3">
                          Rather have it built for you? Tell us your dates and interests.
                        </p>
                        <button
                          type="button"
                          onClick={onBuildItinerary}
                          className="mt-5 inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-magic-line text-base"></i>
                          Build My Itinerary
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  (plan[activeDay] || []).map((id) => {
                    const item = itemById[id];
                    if (!item) return null;
                    return (
                      <div
                        key={id}
                        className="bg-primary-800 border border-primary-700 rounded-md px-4 py-3 flex items-start justify-between gap-3"
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 text-background-50 shrink-0">
                            <i className={`${item.icon} text-lg`}></i>
                          </span>
                          <div className="min-w-0">
                            <p className="font-heading text-sm text-background-50 font-semibold leading-snug">
                              {item.title}
                            </p>
                            <p className="text-xs text-background-300 mt-1">{item.duration}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemove(activeDay, id)}
                          aria-label={`Remove ${item.title}`}
                          className="w-8 h-8 flex items-center justify-center rounded-full text-background-300 hover:text-background-50 hover:bg-primary-700 transition-colors cursor-pointer shrink-0"
                        >
                          <i className="ri-close-line text-lg"></i>
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="mt-6 pt-5 border-t border-primary-800">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-background-300">Estimated time</p>
                    <p className="font-heading text-lg text-background-50 font-semibold">
                      {summary.hours > 0 ? `${summary.hours} hours` : "—"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-background-300">Estimated cost</p>
                    <p className="font-heading text-lg text-accent-300 font-semibold">
                      {summary.cost > 0 ? formatRupees(summary.cost) : "—"}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="text-background-300">
                    Across {dayCount} {dayCount === 1 ? "day" : "days"}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-accent-300">
                    <i className="ri-information-line"></i>
                    Rough budget — final quote after we chat
                  </span>
                </div>

                {totalStops > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {Array.from({ length: dayCount }, (_, d) => d).map((d) => {
                      const daySummary = summarizeDay(plan, d);
                      return (
                        <div key={d} className="bg-primary-800 border border-primary-700 rounded-md px-3 py-2.5">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-background-300">
                            Day {d + 1}
                          </p>
                          <p className="font-heading text-sm text-background-50 font-semibold mt-0.5">
                            {daySummary.cost > 0 ? formatRupees(daySummary.cost) : "—"}
                          </p>
                          <p className="text-[11px] text-background-400 mt-0.5">
                            {daySummary.stops} {daySummary.stops === 1 ? "stop" : "stops"}
                            {daySummary.hours > 0 ? ` · ${daySummary.hours} hrs` : ""}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <p className="flex items-center justify-center gap-1.5 text-xs text-background-300 mt-5">
                <i className="ri-save-3-line text-accent-300"></i>
                Your plan is saved on this device — come back any time.
              </p>

              {totalStops > 0 && (
                <>
                  {onBuildItinerary ? (
                    <button
                      type="button"
                      onClick={onBuildItinerary}
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Have Our Team Refine This
                      <i className="ri-magic-line"></i>
                    </button>
                  ) : (
                    <a
                      href="/contact#contact-form"
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Send This Plan to Our Team
                      <i className="ri-send-plane-fill"></i>
                    </a>
                  )}
                  <p className="text-xs text-background-300 text-center mt-3">
                    Your {totalStops}-stop plan travels with the enquiry — we'll fine-tune dates,
                    timings and logistics with you.
                  </p>
                </>
              )}

              <div className="relative mt-3">
                <button
                  type="button"
                  onClick={() => setShareOpen((open) => !open)}
                  disabled={totalStops === 0}
                  className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-6 py-3.5 rounded-md border transition-colors whitespace-nowrap ${
                    totalStops === 0
                      ? "border-primary-700 text-background-300 cursor-not-allowed"
                      : "border-accent-400 text-accent-300 hover:bg-primary-800 cursor-pointer"
                  }`}
                >
                  <i className="ri-share-2-line text-base"></i>
                  Share / Send My Itinerary
                </button>

                {shareOpen && totalStops > 0 && (
                  <>
                    <button
                      type="button"
                      aria-label="Close share menu"
                      onClick={() => setShareOpen(false)}
                      className="fixed inset-0 z-40 cursor-default"
                    ></button>
                    <div className="absolute bottom-full mb-2 left-0 right-0 z-50 bg-background-50 rounded-md border border-background-200 overflow-hidden">
                      <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setShareOpen(false)}
                        className="flex items-center gap-3 px-4 py-3.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                          <i className="ri-whatsapp-line text-lg"></i>
                        </span>
                        Share on WhatsApp
                      </a>
                      <a
                        href={mailHref}
                        onClick={() => setShareOpen(false)}
                        className="flex items-center gap-3 px-4 py-3.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors border-t border-background-200 cursor-pointer"
                      >
                        <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                          <i className="ri-mail-line text-lg"></i>
                        </span>
                        Share via Email
                      </a>
                      <button
                        type="button"
                        onClick={handleCopyText}
                        className="w-full flex items-center gap-3 px-4 py-3.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors border-t border-background-200 cursor-pointer"
                      >
                        <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                          <i className={copied ? "ri-check-line text-lg" : "ri-file-copy-line text-lg"}></i>
                        </span>
                        {copied ? "Copied to clipboard" : "Copy as plain text"}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {totalStops > 0 ? (
                <Link
                  to="/plan-your-trip/print"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-6 py-3.5 rounded-md border border-primary-700 text-background-100 hover:border-accent-400 hover:text-accent-300 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-printer-line text-base"></i>
                  Print / Save as PDF
                </Link>
              ) : (
                <span className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-6 py-3.5 rounded-md border border-primary-700 text-background-300 opacity-60 cursor-not-allowed whitespace-nowrap">
                  <i className="ri-printer-line text-base"></i>
                  Print / Save as PDF
                </span>
              )}
              <p className="text-xs text-background-300 text-center mt-3">
                {totalStops === 0
                  ? "Add a few stops and you can send or print the day-by-day plan."
                  : "Send, copy or print a day-by-day summary of your plan."}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}